import { useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import type { ChartOptions } from "chart.js";
import ChevronDownIcon from "@/components/SVGComponents/ChevronDownIcon";
import NoDataFound from "@/components/NoDataFound/NoDataFound";
import type { MembershipGrowthPoint } from "@/services/api/dashboard";
import { combineClasses } from "@/lib/utils";
import styles from "./MembershipGrowthChart.module.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// Matches $color-gray-800 / $color-gray-400 / $color-gray-200 / $color-gray-900
// in styles/_variables.scss — those tokens are plain Sass hex, not the
// runtime CSS-var brand bridge, so a literal here stays in sync with them.
const BAR_COLOR = "#15202E";
const AXIS_TICK_COLOR = "#9ca3af";
const TOOLTIP_BORDER_COLOR = "#e5e7eb";
const TOOLTIP_TEXT_COLOR = "#111827";

const getPrimaryColor = () =>
  `rgb(${getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim()})`;

const PERIOD_OPTIONS = [
  { months: 12, label: "12 Months" },
  { months: 6, label: "6 Months" },
  { months: 3, label: "3 Months" },
];

export interface MembershipGrowthChartProps {
  data: MembershipGrowthPoint[];
  isLoading?: boolean;
  period: number;
  onPeriodChange: (period: number) => void;
}

const MembershipGrowthChart = ({ data, isLoading = false, period, onPeriodChange }: MembershipGrowthChartProps) => {
  const lastIndex = data.length - 1;
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const rangeRef = useRef<HTMLDivElement | null>(null);
  const selectedLabel = PERIOD_OPTIONS.find((option) => option.months === period)?.label ?? `${period} Months`;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rangeRef.current && !rangeRef.current.contains(event.target as Node)) {
        setIsRangeOpen(false);
      }
    };

    if (isRangeOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isRangeOpen]);

  const chartData = useMemo(() => {
    const currentMonthColor = getPrimaryColor();
    return {
      labels: data.map((item) => item.month),
      datasets: [
        {
          label: "New members",
          data: data.map((item) => item.newMemberCount),
          backgroundColor: data.map((_, index) => (index === lastIndex ? currentMonthColor : BAR_COLOR)),
          borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
          maxBarThickness: 22,
        },
      ],
    };
  }, [data, lastIndex]);

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#ffffff",
        titleColor: TOOLTIP_TEXT_COLOR,
        bodyColor: TOOLTIP_TEXT_COLOR,
        borderColor: TOOLTIP_BORDER_COLOR,
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        displayColors: false,
        titleFont: { family: "DM Sans", weight: "bold" },
        bodyFont: { family: "DM Sans" },
        callbacks: {
          label: (ctx) => `${ctx.formattedValue} new members`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: AXIS_TICK_COLOR, font: { family: "DM Sans", size: 10 } },
      },
      y: {
        beginAtZero: true,
        display: false,
      },
    },
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Membership Growth</h2>
          <p className={styles.subtitle}>Net new members per month · last {period} months</p>
        </div>
        <div className={styles.rangeSelectWrapper} ref={rangeRef}>
          <button type="button" className={styles.rangeSelect} onClick={() => setIsRangeOpen((prev) => !prev)}>
            {selectedLabel}
            <ChevronDownIcon
              className={styles.rangeChevron}
              style={{ transform: isRangeOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </button>

          {isRangeOpen && (
            <div className={styles.rangeDropdown}>
              {PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.months}
                  type="button"
                  className={combineClasses(styles.rangeOption, option.months === period && styles.rangeOptionActive)}
                  onClick={() => {
                    onPeriodChange(option.months);
                    setIsRangeOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.chartWrapper}>
        {!isLoading && data.length === 0 ? <NoDataFound /> : <Bar data={chartData} options={options} />}
      </div>
    </div>
  );
};

export default MembershipGrowthChart;
